import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { animes } from "@/mocks/anime";
import { newsList } from "@/mocks/news";
import { useFavorites } from "@/hooks/useFavorites";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/category", label: "动漫分类" },
  { to: "/news", label: "动漫资讯" },
  { to: "/about", label: "关于我们" },
  { to: "/contact", label: "联系我们" },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { count } = useFavorites();

  useEffect(() => {
    setMenuOpen(false);
    setSuggestOpen(false);
  }, [location.pathname, location.search]);

  const suggestions = keyword.trim()
    ? [
        ...animes
          .filter((item) => item.title.includes(keyword.trim()))
          .slice(0, 4)
          .map((item) => ({ id: item.id, title: item.title, type: "动漫", to: `/detail/${item.id}` })),
        ...newsList
          .filter((item) => item.title.includes(keyword.trim()))
          .slice(0, 3)
          .map((item) => ({ id: item.id, title: item.title, type: "资讯", to: `/news/${item.id}` })),
      ].slice(0, 6)
    : [];

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    setSuggestOpen(false);
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/80 bg-background-50/95 backdrop-blur-md">
      <div className="flex h-16 w-full items-center gap-4 px-4 md:h-[72px] md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <span className="font-heading text-lg font-bold leading-none">樱</span>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
              樱花动漫
            </span>
            <span className="mt-1 font-label text-[10px] uppercase tracking-[0.22em] text-foreground-500">
              Sakura Anime
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                [
                  "whitespace-nowrap rounded-md px-3.5 py-2 text-sm transition-colors",
                  isActive
                    ? "bg-primary-100 font-medium text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950",
                ].join(" ")
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form onSubmit={handleSearch} className="relative hidden md:block">
            <div className="flex h-10 w-52 items-center gap-2 rounded-full border border-background-300 bg-background-50 px-3.5 transition-colors focus-within:border-primary-300 lg:w-64">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={keyword}
                onChange={(event) => {
                  setKeyword(event.target.value);
                  setSuggestOpen(true);
                }}
                onFocus={() => setSuggestOpen(true)}
                onBlur={() => window.setTimeout(() => setSuggestOpen(false), 150)}
                placeholder="搜索动漫或资讯"
                aria-label="搜索动漫或资讯"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            {suggestOpen && suggestions.length > 0 && (
              <div className="absolute right-0 top-12 w-80 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1.5">
                {suggestions.map((item) => (
                  <Link
                    key={`${item.type}-${item.id}`}
                    to={item.to}
                    className="flex items-center gap-2 px-3 py-2 transition-colors hover:bg-background-100"
                  >
                    <span className="rounded bg-secondary-100 px-1.5 py-0.5 font-label text-[10px] text-secondary-900">
                      {item.type}
                    </span>
                    <span className="truncate text-sm text-foreground-800">{item.title}</span>
                  </Link>
                ))}
              </div>
            )}
          </form>

          <Link
            to="/category?fav=1"
            className="relative hidden h-10 w-10 items-center justify-center rounded-full text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-600 sm:flex"
            aria-label="我的收藏"
          >
            <i className="ri-heart-3-line text-lg" />
            {count > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 font-label text-[10px] text-background-50">
                {count}
              </span>
            )}
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
            aria-label="打开导航菜单"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-3-line text-xl"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 lg:hidden">
          <form onSubmit={handleSearch} className="mb-4">
            <div className="flex h-11 w-full items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索动漫或资讯"
                aria-label="搜索动漫或资讯"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              <button type="submit" className="whitespace-nowrap text-xs font-medium text-primary-600">
                搜索
              </button>
            </div>
          </form>
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  [
                    "flex items-center justify-between rounded-md px-3 py-3 text-sm transition-colors",
                    isActive
                      ? "bg-primary-100 font-medium text-primary-700"
                      : "text-foreground-700 hover:bg-background-100",
                  ].join(" ")
                }
              >
                <span>{item.label}</span>
                <i className="ri-arrow-right-s-line text-base text-foreground-400" />
              </NavLink>
            ))}
            <NavLink
              to="/category?fav=1"
              className="flex items-center justify-between rounded-md px-3 py-3 text-sm text-foreground-700 transition-colors hover:bg-background-100"
            >
              <span>我的收藏</span>
              <span className="font-label text-xs text-primary-600">{count} 部</span>
            </NavLink>
          </nav>
        </div>
      )}
    </header>
  );
}